import React, { useCallback, useEffect, useRef, useState } from "react";
import { CButton, CDropdownItem, CFormInput, CFormTextarea } from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";

import { useRunDetail } from "@src/contexts/RunDetailContext";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { COMMAND_POLL_MAX_ATTEMPTS, EXECUTION_POLL_DELAY_MS } from "@src/utils/constants";
import { delay } from "@src/utils/dateUtils";

interface Props {
  executionId: number;
  quoteCurrency: string | null;
  disabled: boolean;
  onStatusChange: (status: string | null) => void;
}

export const CapitalCommandForm: React.FC<Props> = ({
  executionId,
  quoteCurrency,
  disabled,
  onStatusChange,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [pollingId, setPollingId] = useState<number | null>(null);
  const pollAbortRef = useRef(false);

  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const { run, updateRun } = useRunDetail();
  const execution = run && "capital_target" in run ? run : null;
  const executionMetrics = execution?.live_metrics ?? execution?.metrics;
  const currentStrategyCapital =
    executionMetrics?.ending_capital ??
    executionMetrics?.final_capital ??
    executionMetrics?.invested_capital ??
    null;
  const pendingTarget = execution?.capital_target ?? null;

  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (loading) onStatusChange("Sending...");
    else if (pollingId != null) onStatusChange("Processing...");
    else onStatusChange(null);
  }, [loading, pollingId, onStatusChange]);

  const pollCapitalCommand = useCallback(
    async (commandId: number, targetCapital: number) => {
      setPollingId(commandId);
      pollAbortRef.current = false;
      let reachedTerminalState = false;

      for (let attempt = 0; attempt < COMMAND_POLL_MAX_ATTEMPTS; attempt++) {
        if (pollAbortRef.current) break;
        await delay(EXECUTION_POLL_DELAY_MS);

        try {
          const command = await tdApi.getExecutionCommand(executionId, commandId);
          if (pollAbortRef.current) break;
          if (!command) continue;

          if (command.status === "completed") {
            reachedTerminalState = true;
            const result = command.result;

            try {
              const refreshed = await tdApi.getExecution(executionId, {
                embed: "positions(movements(order)),live_metrics",
              });
              if (refreshed) updateRun(refreshed);
            } catch (fetchErr) {
              console.error("[CapitalCommandForm] Failed to refresh execution:", fetchErr);
            }

            const resultAction = String(result?.action ?? "");
            const resultAmount = typeof result?.amount === "number" ? result.amount : undefined;
            const resultTarget =
              typeof result?.target === "number"
                ? result.target
                : typeof result?.capital_target === "number"
                  ? result.capital_target
                  : targetCapital;
            const cur = quoteCurrency ?? "";
            const fmtAmt = resultAmount?.toFixed(2);
            const fmtTgt = resultTarget.toFixed(2);

            const actionToasts: Record<string, { title: string; message: string }> = {
              increase: {
                title: "Capital deposited",
                message: fmtAmt
                  ? `Deposited ${fmtAmt} ${cur} to reach target ${fmtTgt} ${cur}.`
                  : `Target set to ${fmtTgt} ${cur}.`,
              },
              decrease_flat: {
                title: "Capital withdrawn",
                message: fmtAmt
                  ? `Withdrew ${fmtAmt} ${cur} to reach target ${fmtTgt} ${cur}.`
                  : `Target set to ${fmtTgt} ${cur}.`,
              },
              decrease_deployed: {
                title: "Capital target set",
                message: `Target set to ${fmtTgt} ${cur}. Withdrawal will be applied as positions close.`,
              },
              no_change: {
                title: "No capital change",
                message: "Target already matches current strategy capital.",
              },
              cancel: {
                title: "Capital target cancelled",
                message: "Pending capital target cleared.",
              },
            };

            const { title: successTitle, message: successMessage } = actionToasts[resultAction] ?? {
              title: "Capital updated",
              message: "Capital target processed successfully",
            };

            showToast({
              type: "success",
              title: successTitle,
              message: successMessage,
              timeout: 10000,
            });
            break;
          } else if (command.status === "failed") {
            reachedTerminalState = true;
            showToast({
              type: "danger",
              title: "Capital update failed",
              message: command.error_message || "Unknown error",
              timeout: 15000,
            });
            break;
          }
        } catch (err) {
          console.error("[CapitalCommandForm] Capital polling error:", err);
        }
      }

      if (!reachedTerminalState && !pollAbortRef.current) {
        showToast({
          type: "warning",
          title: `Command #${commandId} Timeout`,
          message: "Command is still processing. Check worker logs.",
          timeout: 15000,
        });
      }

      if (!pollAbortRef.current) {
        setPollingId(null);
      }
    },
    [executionId, quoteCurrency, showToast, updateRun],
  );

  const handleSubmit = async () => {
    const targetValue = parseFloat(target);
    if (!isFinite(targetValue) || targetValue < 0.01) {
      showToast({
        type: "warning",
        title: "Invalid target",
        message: "Enter a positive target amount",
        timeout: 4000,
      });
      return;
    }

    const currency = quoteCurrency ?? "???";
    const currentLabel =
      currentStrategyCapital != null
        ? `${currentStrategyCapital.toFixed(2)} ${currency}`
        : "unknown";

    const { confirmed } = await confirmModal(
      `Set capital target to ${targetValue.toFixed(2)} ${currency}?${reason ? `\n\nReason: ${reason}` : ""}\n\nCurrent capital: ${currentLabel}`,
      { title: "Set Capital Target" },
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const command = await tdApi.createExecutionCommand(executionId, {
        command_type_name: "set-capital-target",
        payload: {
          target_capital: targetValue,
          ...(reason && { reason }),
        },
      });

      if (!command?.id) throw new Error("Command created but no ID returned");

      showToast({
        type: "info",
        title: `Command #${command.id} Sent`,
        message: `Target: ${targetValue.toFixed(2)} ${currency} — waiting for worker...`,
        timeout: 5000,
      });

      setTarget("");
      setReason("");
      setShowForm(false);

      pollCapitalCommand(command.id, targetValue);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({
        type: "danger",
        title: "Command Failed",
        message: `Failed to send capital command: ${message}`,
        timeout: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTarget = async () => {
    if (pendingTarget == null) return;

    const { confirmed } = await confirmModal(
      `Cancel pending capital target of ${pendingTarget.toFixed(2)} ${quoteCurrency ?? ""}?`,
      { title: "Cancel Capital Target" },
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const command = await tdApi.createExecutionCommand(executionId, {
        command_type_name: "set-capital-target",
        payload: { target_capital: null, ...(reason && { reason }) },
      });

      if (!command?.id) throw new Error("Command created but no ID returned");

      showToast({
        type: "info",
        title: `Command #${command.id} Sent`,
        message: "Cancelling pending target...",
        timeout: 5000,
      });

      pollCapitalCommand(command.id, 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({
        type: "danger",
        title: "Command Failed",
        message: `Failed to cancel target: ${message}`,
        timeout: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  // `disabled` on CDropdownItem is only a stylesheet rule, so the handler refuses on its own.
  const handleToggle = () => {
    if (disabled) return;
    setShowForm((prev) => !prev);
  };

  return (
    <>
      <CDropdownItem className="cursor-pointer" onClick={handleToggle} disabled={disabled}>
        Capital {showForm ? "▴" : "▾"}
      </CDropdownItem>
      {showForm && (
        <div className="capitalCommandForm px-3 py-2">
          {currentStrategyCapital != null && (
            <div className="capitalCommandForm-current mb-2">
              <small className="text-body-secondary">
                Current:
                <br />
                {currentStrategyCapital.toFixed(2)} {quoteCurrency ?? ""}
              </small>
            </div>
          )}
          {pendingTarget != null && (
            <div className="capitalCommandForm-pending mb-2 d-flex align-items-center justify-content-between">
              <small className="text-warning">
                Pending: {pendingTarget.toFixed(2)} {quoteCurrency ?? ""}
              </small>
              <CButton
                color="warning"
                variant="ghost"
                size="sm"
                className="p-0"
                onClick={handleCancelTarget}
                disabled={disabled}
              >
                Cancel
              </CButton>
            </div>
          )}
          <div className="capitalCommandForm-field mb-2">
            <small className="capitalCommandForm-label text-body-secondary">
              Target Capital ({quoteCurrency ?? "..."})
            </small>
            <CFormInput
              type="number"
              size="sm"
              placeholder={
                currentStrategyCapital != null ? currentStrategyCapital.toFixed(2) : "1000.00"
              }
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              min={0}
              step={0.01}
            />
          </div>
          <div className="capitalCommandForm-field mb-2">
            <small className="capitalCommandForm-label text-body-secondary">
              Reason (optional)
            </small>
            <CFormTextarea
              placeholder="optional reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
            />
          </div>
          <CButton
            color="primary"
            size="sm"
            className="w-100"
            onClick={handleSubmit}
            disabled={disabled || !target || !quoteCurrency}
          >
            {loading ? <CSpinnerText text="Sending..." /> : "Set Target"}
          </CButton>
        </div>
      )}
    </>
  );
};
